import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import CircleStackIcon from '@heroicons/react/24/solid/CircleStackIcon';
import ClockIcon from '@heroicons/react/24/solid/ClockIcon';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import MoreVert from "@mui/icons-material/MoreVert";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';
import { usePopover } from '../hooks/usePopover';
import { FilePopover } from './FilePopover';
import { FilePopoverAdmin } from './FilePopoverAdmin';
import { formatSizeUnits } from '../utils/fileManagement';
import { FileIcon } from '../fileIcon/FileIcon';
import defaultStyles from '../fileIcon/defaultStyles';
import { getRequestCountByData } from "../utils/requestManagement";
import { getPresignedURLForViewer, isViewerReady, isViewerError } from '../utils/interfaceS3';
import ErrorViewerDialog from '../dialog/ErrorViewerDialog.jsx';
import { AuthContext } from '../context/AuthContext';
import { CloudContext } from '../context/CloudContext';
import { encodeTransform } from '../viewer/utils/transformationCodec';

/**
 * Unified file card component that displays file information in a card layout
 * Handles both regular user view and admin view with conditional rendering
 * 
 * @param {Object} data - File data object (structure varies by isAdmin flag)
 * @param {Function} reloadProjects - Callback to refresh projects list after file operations
 * @param {Array} processings - Available processing options for the file
 * @param {boolean} isAdmin - Flag to enable admin-specific features and data structure
 * @param {string|number} userid - User ID for admin operations
 */
export default function FileCard({ 
    data, 
    reloadProjects, 
    processings, 
    isAdmin = false, 
    userid 
}) {

    const smUp = useMediaQuery((theme) => theme.breakpoints.up('xs'));
    const filePopover = usePopover();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, idToken, service } = useContext(AuthContext);
    const { region, idpool, cesium_token } = useContext(CloudContext);
    const { enqueueSnackbar } = useSnackbar();

    const [isHovered, setIsHovered] = useState(false);
    const [isViewerLoading, setIsViewerLoading] = useState(false);
    const [openDialogErrorViewer, setOpenDialogErrorViewer] = useState(false);

    /**
     * Normalize data access patterns between admin and regular user views
     * Admin data: direct properties (data.filename)
     * Regular data: nested attributes (data.attributes.filename)
     */
    const fileData = {
        filename: isAdmin ? data.filename : data.attributes?.filename,
        description: isAdmin ? data.description : data.attributes?.description,
        imageUrl: isAdmin ? data.imageUrl : data.attributes?.imageUrl,
        createdAt: isAdmin ? data.createdAt : data.attributes?.createdAt,
        size: isAdmin ? data.size : data.attributes?.size,
        viewedByAdmin: data.viewedByAdmin,
        resultsCount: isAdmin ? 0 : (data.attributes?.results?.data?.length || 0),
    };

    /**
     * Formats ISO date string to localized date format
     * @param {string} dateStr - ISO date string
     * @returns {string} Formatted date string
     */
    const getFormattedDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString();
    };

    const isArchive = fileData.filename?.toLowerCase().endsWith('.zip');

    async function handleView() {
        if (service === 'classic') {
            setIsViewerLoading(true);
            const viewerReady = await isViewerReady(user.sub, idToken, region, idpool, fileData.filename);
            if (viewerReady) {
                const { metadata, hierarchy, octree } = await getPresignedURLForViewer(
                    user.sub, idToken, region, idpool, fileData.filename, 86400
                );
                setIsViewerLoading(false);
                navigate('/viewer-page/', {
                    state: { filename: fileData.filename, description: fileData.description, metadata, hierarchy, octree }
                });
            } else {
                const viewererror = await isViewerError(user.sub, idToken, region, idpool, fileData.filename);
                setIsViewerLoading(false);
                if (viewererror) {
                    setOpenDialogErrorViewer(true);
                } else {
                    enqueueSnackbar(t('ViewerNotReady'), { variant: 'info' });
                }
            }
        } else if (service === 'mapping') {
            const resultsIdCesium = (data.attributes?.results?.data || [])
                .map(result => result.attributes.id_cesium)
                .filter(id => id != null);
            const pointTransform = data.attributes?.transformation_cesium_points?.data;
            const gsTransform    = data.attributes?.transformation_cesium_gs?.data;
            navigate('/cesium-viewer-page/', {
                state: {
                    mappingAssetId: data.attributes?.id_cesium,
                    gaussianSplattingId: data.attributes?.id_cesium_gs,
                    resultsAssetId: resultsIdCesium,
                    cesium_token,
                    transformationPointsId:  pointTransform?.id ?? null,
                    transformationPointsStr: pointTransform ? encodeTransform(pointTransform.attributes) : null,
                    transformationGsId:      gsTransform?.id ?? null,
                    transformationGsStr:     gsTransform ? encodeTransform(gsTransform.attributes) : null,
                }
            });
        }
    }

    /**
     * Opens the file actions popover menu
     */
    const handleClickVertButton = () => {
        filePopover.handleOpen();
    };

    // Select appropriate popover component based on user role
    const PopoverComponent = isAdmin ? FilePopoverAdmin : FilePopover;

    return (
        <>
            <Card
                sx={{
                    display: 'flex',
                    position: 'relative',
                    overflow: isAdmin ? 'visible' : 'hidden',
                    flexDirection: 'column',
                    height: '100%',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-3px) scale(1.02)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                        outline: '1px solid',
                        outlineColor: 'rgba(132, 132, 132, 0.15)',
                    },
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {isAdmin && (
                    <Badge
                        badgeContent={getRequestCountByData(data)}
                        color="warning"
                        sx={{
                            position: 'absolute',
                            top: 20,
                            left: 20,
                            zIndex: 1000,
                        }}
                    />
                )}
                <IconButton
                    aria-label="settings"
                    onClick={handleClickVertButton}
                    ref={filePopover.anchorRef}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 4, zIndex: 1 }}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
                <CardHeader
                    sx={{
                        display: "flex",
                        overflow: "hidden",
                        minHeight: 100,
                        px: 2,
                        "& .MuiCardHeader-content": {
                            overflow: "hidden"
                        },
                    }}
                    title={
                        <Typography
                            align="center"
                            gutterBottom
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {fileData.filename}
                        </Typography>
                    }
                    subheader={
                        <Typography
                            align="center"
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {fileData.description}
                        </Typography>
                    }
                />
                {smUp && (
                    <>
                        {fileData.imageUrl ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    px: 2, pb: 0
                                }}
                            >
                                <Box sx={{ position: 'relative', width: '100%', borderRadius: '12px', overflow: 'hidden' }}>
                                    <CardMedia
                                        image={fileData.imageUrl}
                                        sx={{
                                            width: "100%",
                                            height: 0,
                                            paddingBottom: "min(75%, 240px)",
                                            backgroundColor: "rgba(0,0,0,0.08)",
                                            boxShadow: '0 5px 22px rgba(0, 0, 0, 0.3)',
                                            transform: isHovered ? 'scale(1.04)' : 'scale(1)',
                                            transition: 'transform 0.35s ease',
                                        }}
                                    />
                                    {!isAdmin && !isArchive && isHovered && (
                                        <Box sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(0, 0, 0, 0.15)',
                                            backdropFilter: 'blur(2px)',
                                        }}>
                                            <Button                                          
                                                variant="contained"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={(e) => { e.stopPropagation(); handleView(); }}
                                                sx={{
                                                    fontWeight: 600,
                                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                                    color: '#fff',
                                                    boxShadow: 'none',
                                                    transition: 'background-color 0.15s ease',
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.45)', boxShadow: 'none' },
                                                }}
                                            >
                                                {t('View')}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                {fileData.resultsCount > 0 && (
                                    <Chip
                                        label={`${fileData.resultsCount} ${fileData.resultsCount === 1 ? t("Product") : t("Products")}`}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            bottom: 8,
                                            right: 24,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            backdropFilter: 'blur(4px)',
                                        }}
                                    />
                                )}
                            </Box>
                        ) : (
                            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            px: 10
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                maxWidth: '100px',
                                                transform: isHovered ? 'scale(1.06)' : 'scale(1)',
                                                transition: 'transform 0.35s ease',
                                            }}
                                        >
                                            <FileIcon
                                                extension={`${fileData.filename.split('.').pop()}`}
                                                {...defaultStyles[`${fileData.filename.split('.').pop()}`]}
                                            />
                                        </Box>
                                    </Box>
                                </CardContent>
                                <Box sx={{ flexGrow: 1 }} />
                                {!isAdmin && !isArchive && isHovered && (
                                    <Box sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 2,
                                    }}>
                                        <Box sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(0, 0, 0, 0.15)',
                                            backdropFilter: 'blur(2px)',
                                            borderRadius: 2
                                        }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={(e) => { e.stopPropagation(); handleView(); }}
                                                sx={{
                                                    fontWeight: 600,
                                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                                    color: '#fff',
                                                    boxShadow: 'none',
                                                    transition: 'background-color 0.15s ease',
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.45)', boxShadow: 'none' },
                                                }}
                                            >
                                                {t('View')}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                                {fileData.resultsCount > 0 && (
                                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'flex-end', px: 2, pb: 1 }}>
                                        <Chip
                                            label={`${fileData.resultsCount} ${fileData.resultsCount === 1 ? t("Product") : t("Products")}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', fontWeight: 600, mr: 1, backgroundColor: 'background.paper' }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </>
                )}
                {!smUp && isAdmin && (
                    <Box sx={{ p: 1 }}></Box>
                )}
                {fileData.imageUrl && <Box sx={{ flexGrow: 1 }} />}
                {!fileData.imageUrl && <Divider />}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ p: 2, alignItems: 'center', justifyContent: 'space-between' }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center' }}
                    >
                        <SvgIcon
                            color="action"
                            fontSize="small"
                        >
                            <ClockIcon />
                        </SvgIcon>
                        <Typography
                            color="text.secondary"
                            display="inline"
                            variant="body2"
                        >
                            {getFormattedDate(fileData.createdAt)}
                        </Typography>
                        {isAdmin && fileData.viewedByAdmin === false && (
                            <Chip label="New" color="error" />
                        )}
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center' }}
                    >
                        <SvgIcon
                            color="action"
                            fontSize="small"
                        >
                            <CircleStackIcon />
                        </SvgIcon>
                        <Typography
                            color="text.secondary"
                            display="inline"
                            variant="body2"
                        >
                            {formatSizeUnits(fileData.size)}
                        </Typography>
                    </Stack>
                </Stack>
            </Card>
            <PopoverComponent
                anchorEl={filePopover.anchorRef.current}
                open={filePopover.open}
                onClose={filePopover.handleClose}
                data={data}
                reloadProjects={reloadProjects}
                processings={processings}
                userid={userid}
            />
            {!isAdmin && (
                <>
                    <Backdrop
                        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                        open={isViewerLoading}
                    >
                        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                            <CircularProgress color="inherit" size="5rem" />
                            <Typography variant="h5" color="inherit">
                                <b>{t('GettingViewerReady')}</b>
                            </Typography>
                        </Stack>
                    </Backdrop>
                    <ErrorViewerDialog open={openDialogErrorViewer} setOpen={setOpenDialogErrorViewer} />
                </>
            )}
        </>
    );
};

FileCard.propTypes = {
    data: PropTypes.shape({
        // For regular components (data.attributes structure)
        attributes: PropTypes.shape({
            filename: PropTypes.string,
            description: PropTypes.string,
            imageUrl: PropTypes.string,
            createdAt: PropTypes.string,
            size: PropTypes.string,
        }),
        // For admin components (direct properties)
        filename: PropTypes.string,
        description: PropTypes.string,
        imageUrl: PropTypes.string,
        createdAt: PropTypes.string,
        size: PropTypes.string,
        viewedByAdmin: PropTypes.bool,
    }).isRequired,
    reloadProjects: PropTypes.func,
    processings: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        description: PropTypes.string,
        url: PropTypes.string,
        output: PropTypes.string,
        subProcessings: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            title: PropTypes.string,
            description: PropTypes.string,
        }))
    })),
    isAdmin: PropTypes.bool,
    userid: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};